import { coerceNumberProperty } from '@angular/cdk/coercion';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Subject } from 'rxjs';

const DEFAULT_PAGE_SIZE = 50;

export interface QMSPaginationOption {
  pageSize?: number;
  pageSizeOptions?: number[];
  length: number;
}

export enum PaginationSize {
  small = 'small',
  medium = 'medium',
  large = 'large',
  mobile = 'mobile',
}
@Component({
  selector: 'qms-paginator',
  templateUrl: './qms-paginator.component.html',
  styleUrls: ['./qms-paginator.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatSelectModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    NgIf,
    NgFor,
    TranslateModule,
    AngularSvgIconModule,
    RouterModule,
  ],
  standalone: true,
})
export class QMSPaginatorComponent implements OnInit {
  sizeMedium: string = 'medium';
  sizeSmall: string = 'small';
  sizeLarge: string = 'large';
  sizeMobile: string = 'mobile';
  LANG: any;

  @Input() numHidden: number = 10;
  @Input() size = 'medium';
  @Input()
  get pageSizeOptions(): number[] {
    return this._pageSizeOptions;
  }
  set pageSizeOptions(value: number[]) {
    this._pageSizeOptions = (value || []).map((p) => coerceNumberProperty(p));
  }
  private _pageSizeOptions: number[] = [10, 25, 100];

  @Input()
  get pageSize(): number {
    return this._pageSize;
  }
  set pageSize(value: number) {
    this._pageSize = Math.max(coerceNumberProperty(value), 0);
    this._updateDisplayedPageSizeOptions();
  }
  private _pageSize: number = 10;

  @Input()
  get length(): number {
    return this._length;
  }
  set length(value: number) {
    this._length = coerceNumberProperty(value);
    this.getNumberOfPages();
  }
  private _length = 0;

  @Input()
  get pageIndex(): number {
    return this._pageIndex + 1;
  }
  set pageIndex(value: number) {
    this.previousPageIndex = this._pageIndex;
    this._pageIndex = Math.max(coerceNumberProperty(value - (this.isFirstLoad ? 0 : 1)), 0);
    this.displayNumberOfPages = this.pagination(this._pageIndex, this.numberOfPages.length);

    this.getNumberOfPages();
  }
  _pageIndex = 0;

  @Output() readonly page: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  previousPageIndex?: number = 0;
  numberOfPages: number[] = [];
  displayNumberOfPages: (string | number)[] = [];
  minPage!: number;
  maxpage!: number;
  isFirstLoad: boolean = true;
  ngUnsubscribe = new Subject<void>();

  private _overlayPanelClass!: string[] | string;
  @Input('overlayPanelClass')
  set overlayClass(value: string | string[]) {
    this._overlayPanelClass = value;
  }
  get overlayClass(): string[] | string {
    const defaultClass: string[] = ['qms-select-panel'];
    if (typeof this._overlayPanelClass === 'string') return [...defaultClass, this._overlayPanelClass];
    if (Array.isArray(this._overlayPanelClass)) return [...defaultClass, ...this._overlayPanelClass];
    return defaultClass;
  }

  constructor() {}
  ngOnInit(): void {
    const langDefault = 'en';
    // this.trans.getLanguageSubject$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res) => {
    //   this.LANG = this.trans.getObjectLang(res || langDefault);
    // });
    this.getNumberOfPages();
    this.isFirstLoad = false;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private _updateDisplayedPageSizeOptions() {
    if (!this.pageSize) {
      this._pageSize = this.pageSizeOptions.length != 0 ? this.pageSizeOptions[0] : DEFAULT_PAGE_SIZE;
    }
  }

  getNumberOfPages(): void {
    if (!this.pageSize) {
      return;
    }
    this.numberOfPages = [];
    const Arrlength = Math.ceil(this._length / this._pageSize);
    for (let index = 1; index <= Arrlength; index++) {
      this.numberOfPages.push(index);
    }
    this.minPage = 1;
    this.maxpage = this.numberOfPages.length ? this.numberOfPages.length : 1;
    this.renderDisplayNumberOfpages();
  }

  renderDisplayNumberOfpages() {
    if (this.numberOfPages.length <= 5) {
      this.displayNumberOfPages = this.numberOfPages.map((x) => x.toString());
      return;
    }
    this.displayNumberOfPages = this.numberOfPages.map((x) => x.toString());
    this.displayNumberOfPages = this.pagination(this.pageIndex, this.numberOfPages.length);
  }
  changePage(page: number): void {
    this.pageIndex = page;
    this._emitPageEvent(this.previousPageIndex);
    this.displayNumberOfPages = this.pagination(parseInt(page.toString()), this.numberOfPages.length);
  }

  pagination(c: number, m: number) {
    let current = c;
    let last = m;
    let delta = 1;
    if (current == this.minPage || current == this.maxpage) {
      delta = 3;
    } else if (current == this.minPage + 1) {
      delta = 2;
    } else if (current == this.maxpage - 1) {
      delta = 2;
    }

    let left = current - delta;
    let right = current + delta + 1;
    let range = [];
    let rangeWithDots = [];
    let l;

    for (let i = 1; i <= last; i++) {
      if (i == 1 || i == last || (i >= left && i < right)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  }
  nextPage(): void {
    this.previousPageIndex = this.pageIndex;
    this.pageIndex = this.pageIndex + 1;
    this._emitPageEvent(this.previousPageIndex);

    this.displayNumberOfPages = this.pagination(this.pageIndex, this.numberOfPages.length);
  }
  previousPage(): void {
    this.previousPageIndex = this.pageIndex;
    this.pageIndex = this.pageIndex - 1;
    this._emitPageEvent(this.previousPageIndex);

    this.displayNumberOfPages = this.pagination(this.pageIndex, this.numberOfPages.length);
  }

  _changePageSize(pageSize: number) {
    const startIndex = this._pageIndex * this.pageSize;
    const previousPageIndex = this._pageIndex;

    this.previousPageIndex = previousPageIndex;
    this.pageIndex = (Math.floor(startIndex / pageSize) || 0) + 1;
    this.pageSize = pageSize;

    this._emitPageEvent(this.previousPageIndex);
    this.getNumberOfPages();
  }

  _changePageNumber(pageNumber: number): void {
    this.previousPageIndex = this.pageIndex;
    this.pageIndex = pageNumber;
    this._emitPageEvent(this.previousPageIndex);
    this.getNumberOfPages();
  }

  private _emitPageEvent(previousPageIndex: number | undefined) {
    this.page.emit({
      previousPageIndex,
      pageIndex: this._pageIndex,
      pageSize: this.pageSize,
      length: this.length,
    });
  }

  ngAfterViewChecked() {}
}
